import { CommonModule } from '@angular/common'
import {
    Component,
    computed,
    Host,
    input,
    OnInit,
    Optional,
    signal,
    SkipSelf,
} from '@angular/core'
import {
    ControlContainer,
    FormGroup,
    ReactiveFormsModule,
} from '@angular/forms'
import { NgIcon } from '@ng-icons/core'

@Component({
    selector: 'lib-user-info-form',
    standalone: true,
    imports: [CommonModule, ReactiveFormsModule, NgIcon],
    templateUrl: './user-info-form.component.html',
    viewProviders: [
        {
            provide: ControlContainer,
            useFactory: (container: ControlContainer) => container,
            deps: [[new SkipSelf(), ControlContainer]],
        },
    ],
})
export class UserInfoFormComponent implements OnInit {
    public readonly theme = input<'auth' | 'dialog'>('auth')
    public readonly disabled = input<boolean>(false)

    readonly passwordVisible = signal(false)

    readonly passwordInputType = computed(() =>
        this.passwordVisible() ? 'text' : 'password',
    )

    readonly passwordIcon = computed(() =>
        this.passwordVisible() ? 'lucideEye' : 'lucideEyeOff',
    )

    parentFormGroup!: FormGroup

    constructor(
        @Optional()
        @Host()
        @SkipSelf()
        private controlContainer: ControlContainer,
    ) {}

    ngOnInit() {
        if (
            this.controlContainer &&
            this.controlContainer.control instanceof FormGroup
        ) {
            this.parentFormGroup = this.controlContainer.control
        } else {
            console.error(
                'UserInfoFormComponent must be used within a FormGroupDirective',
            )
        }
    }

    get formGroup(): FormGroup {
        return this.parentFormGroup
    }

    togglePasswordVisibility() {
        this.passwordVisible.update((v) => !v)
    }

    get passwordValue(): string {
        return this.formGroup?.get('password')?.value || ''
    }

    hasLowerCase(str: string): boolean {
        return /[a-z]/.test(str)
    }

    hasUpperCase(str: string): boolean {
        return /[A-Z]/.test(str)
    }

    hasNumber(str: string): boolean {
        return /\d/.test(str)
    }

    hasSpecialChar(str: string): boolean {
        return /[!@#$%^&*]/.test(str)
    }

    get hasMinLength(): boolean {
        return this.passwordValue.length >= 8
    }

    get hasLower(): boolean {
        return this.hasLowerCase(this.passwordValue)
    }

    get hasUpper(): boolean {
        return this.hasUpperCase(this.passwordValue)
    }

    get strengthScore(): number {
        if (!this.passwordValue) return 0
        let score = 0
        if (this.hasMinLength) score++
        if (this.hasLower && this.hasUpper) score++
        if (this.hasNumber(this.passwordValue)) score++
        if (this.hasSpecialChar(this.passwordValue)) score++
        return score
    }

    get strengthLabel(): string {
        switch (this.strengthScore) {
            case 0:
            case 1:
                return 'Weak'
            case 2:
                return 'Fair'
            case 3:
                return 'Good'
            case 4:
                return 'Strong'
            default:
                return ''
        }
    }

    get strengthColorClass(): string {
        switch (this.strengthScore) {
            case 1:
                return 'bg-rose-500'
            case 2:
                return 'bg-amber-500'
            case 3:
                return 'bg-blue-500'
            case 4:
                return 'bg-emerald-500'
            default:
                return 'bg-slate-200'
        }
    }
}
